import * as React from 'react';
import { useRouter } from 'next/router';
import { Compass, FileText } from 'lucide-react';
import { Button, Typography } from '@/components/common';
import {
	ActionRequiredRowProps,
	TakeTheTourRowProps,
	VerifyIdentityRowProps,
} from '@/interfaces';
import {
	getActionRequiredActionLabel,
	getActionRequiredDescription,
	getActionRequiredHref,
	getActionRequiredIds,
	getDeadlineLabel,
	invalidRoutes,
	isPastDue,
} from '@/utils/actionRequired';
import { CasePill } from './CasePill';

const ROW_CLASSNAME =
	'flex flex-col gap-4 border-b border-grey-100 bg-grey-2 p-4 last:border-b-0 xl:flex-row xl:items-center xl:justify-between';

const BUTTON_CLASSNAME = 'md:self-start xl:self-auto xl:shrink-0';

const BUTTON_LABEL_CLASSNAME = 'max-w-44 truncate sm:max-w-xs';

const ICON_BOX_CLASSNAME =
	'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#8039DF1A] text-primary';

const DeadlinePill: React.FC<{ dueDate?: string }> = ({ dueDate }) => {
	if (!dueDate) return null;

	return (
		<CasePill tone={isPastDue(dueDate) ? 'danger' : 'info'}>
			{getDeadlineLabel(dueDate)}
		</CasePill>
	);
};

const RowText: React.FC<{
	title: string;
	description?: string;
	dueDate?: string;
	caseName?: string;
}> = ({ title, description, dueDate, caseName }) => (
	<div className="flex min-w-0 flex-1 flex-col gap-1">
		<div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
			<span title={title} className="min-w-0 max-w-full">
				<Typography type="semibold-16" className="truncate">
					{title}
				</Typography>
			</span>
			<DeadlinePill dueDate={dueDate} />
		</div>
		{caseName ? (
			<Typography
				type="regular-14"
				className="line-clamp-2 break-words text-gray-35"
			>
				<span className="text-primary">{caseName}</span>
				{description && (
					<>
						<span
							aria-hidden="true"
							className="mx-2 inline-block h-3.5 w-px bg-[#6E6A73] align-middle"
						/>
						{description}
					</>
				)}
			</Typography>
		) : (
			description && (
				<Typography
					type="regular-14"
					className="line-clamp-2 break-words text-gray-35"
				>
					{description}
				</Typography>
			)
		)}
	</div>
);

export const ActionRequiredRow: React.FC<ActionRequiredRowProps> = ({
	item,
	showCaseName = false,
	onOpen,
	onSelect,
}) => {
	const router = useRouter();
	const { caseId, appId } = getActionRequiredIds(item);
	const buttonLabel = getActionRequiredActionLabel(item);

	return (
		<li className={ROW_CLASSNAME}>
			<div className="flex min-w-0 flex-1 gap-3">
				<span aria-hidden="true" className={ICON_BOX_CLASSNAME}>
					<FileText width={20} height={20} />
				</span>
				<RowText
					title={item.name}
					description={getActionRequiredDescription(item)}
					dueDate={item.due_date}
					caseName={showCaseName ? item.case?.case_title : undefined}
				/>
			</div>
			<Button
				label={buttonLabel}
				decoration="primary"
				size="medium"
				className={BUTTON_CLASSNAME}
				classNameLink={BUTTON_CLASSNAME}
				classNameLabel={BUTTON_LABEL_CLASSNAME}
				href={getActionRequiredHref(item)}
				onClick={() => {
					onOpen(
						item.action_type,
						item.id,
						caseId,
						appId,
						item.url,
						item.criteria?.instructions
					);
					onSelect(item);
				}}
				disabled={invalidRoutes.some((route) =>
					router.pathname.includes(route)
				)}
			/>
		</li>
	);
};

export const VerifyIdentityRow: React.FC<VerifyIdentityRowProps> = ({
	onVerify,
}) => (
	<li className={ROW_CLASSNAME}>
		<div className="flex min-w-0 flex-1 gap-3">
			<span aria-hidden="true" className={ICON_BOX_CLASSNAME}>
				<FileText width={20} height={20} />
			</span>
			<RowText
				title="Verify your identity"
				description="We can’t move your case forward until we’ve confirmed your identity."
			/>
		</div>
		<Button
			label="Verify Identity"
			decoration="primary"
			size="medium"
			className={BUTTON_CLASSNAME}
			classNameLabel={BUTTON_LABEL_CLASSNAME}
			onClick={onVerify}
		/>
	</li>
);

export const TakeTheTourRow: React.FC<TakeTheTourRowProps> = ({ onStart }) => (
	<li className={ROW_CLASSNAME}>
		<div className="flex min-w-0 flex-1 items-center gap-3">
			<span aria-hidden="true" className={ICON_BOX_CLASSNAME}>
				<Compass size={18} />
			</span>
			<Typography
				type="semibold-16"
				className="line-clamp-2 min-w-0 break-words"
			>
				Get started with Claim Magic
			</Typography>
		</div>
		<Button
			label="Take the Tour"
			decoration="primary"
			size="medium"
			className={BUTTON_CLASSNAME}
			classNameLabel={BUTTON_LABEL_CLASSNAME}
			onClick={onStart}
		/>
	</li>
);
